import { Box } from '@mui/material';
import {
    DataGridProProps,
    GridActionsCellItem,
    GridCallbackDetails,
    GridColDef,
    GridColumnResizeParams,
    GridRowParams,
    GridRowsProp,
    GridSelectionModel,
    useGridApiRef,
} from '@mui/x-data-grid-pro';
import clsx from 'clsx';
import makeStyles from '@mui/styles/makeStyles';
import React, { useEffect, useState } from 'react';
import EditPanelContextMenu from './EditPanelCtxMenu';
import Controls from '../../controls/Controls';
import { CustomErrorOverlay, GridNoRowsCustomOverlay } from '../AdminDataGrid/AdminDataGridCustomComponents';
import { EditPanelDataGridToolbar } from './EditPanelDataGridToolbar';
import * as MaterialUIIcons from '../../controls/MaterialUIIcons';
import { useStyles } from '../AdminDataGrid/AdminDataGridStyles';
import { useDataGridSettings } from '../AdminDataGrid/AdminDataGridHooks';
import { getGridCheckboxColDef } from './CustomComponents';
import { useRefWithCallback } from '../../../utils/utilHooks';
import { initDefaultMessageDialogState, MessageDialog, MessageDialogState, } from '../MessageDialog/MessageDialog';
import { CtxMenuItem } from '../AdminDataGrid/AdminDataGridCtxMenu';

export type EditPanelDataGridProps<T> = DataGridProProps &
    ColumnSettingsProp & {
        rows: GridRowsProp;
        headerLabel?: string;
        canEdit?: boolean;
        refetch?: () => void;
        refreshTooltip?: string;
        useRefresh?: boolean;
        testId?: string;
        height?: string | 'inherit';
        error?: string;
        hideToolbar?: boolean;
        rowsPerPageOptions?: number[] | undefined;
        disabled?: boolean;
        contentContextMenu?: React.ReactElement | React.ReactElement[];
        getCurrentItemCtxMenu?: (selected: T) => void;
        isEnabledRowCtxClick?: (selected: T) => boolean;
        enableContextMeny?: boolean;
        toolbarPagination?: boolean;
        setPageSize?: (size: number) => void;
        setSelectedRows?: (value: T[]) => void;
        disableColumnVisibilitySaving?: boolean;
        selectionModel?: GridSelectionModel;
        message?: MessageDialogState;
        configureColumns?: (columns: GridColDef[]) => GridColDef[];
        toolbarRowCount?: boolean;
        searchMode?: boolean;
        hideNoRows?: boolean;
        confCtxMenuItems?: (items: CtxMenuItem<T>[]) => CtxMenuItem<T>[];
    };

type ColumnSettingsProp =
    | { disableColumnSettings?: true; gridId?: string }
    | GridIdProp;
type GridIdProp = { gridId: string; disableColumnSettings?: false };

const useGridStyles = makeStyles((theme) => ({
    editPanelContainer: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        display: 'flex',
        flexDirection: 'row',
        flex: '1'
    },
    pagination: {
        '& .MuiTablePagination-toolbar': {
            minHeight: 'fit-content',
            '& .MuiSvgIcon-colorAction': {
                color: 'inherit',
            },
        },
    },
    root: {

        '& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus-within':
        {
            outline: 'none',
        },
        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': {
            outline: 'none',
        },
        '& .MuiDataGrid-footerContainer': {
            height: '36px',
            minHeight: '36px !important'
        },

        '& .MuiDataGrid-cellCheckbox': {
            '& .MuiCheckbox-root.Mui-disabled': {
                '& .MuiSvgIcon-root': {
                    color: theme.palette.grey[400],
                },
            },
        },
        '&.MuiDataGrid-root': {
            padding: theme.spacing(0),
        },
        '&.MuiDataGrid-root .MuiDataGrid-row.Mui-selected': {
            background: 'none',
        }, '&.MuiDataGrid-root .MuiDataGrid-row.Mui-hovered': {
            backgroundColor: 'rgb(0,0,0,0.04)'
        },
    },
    removeEmptySpace: {
        '& .MuiDataGrid-virtualScrollerContent': {
            height: 'auto !important'
        }
    }
}));

export function EditPanelDataGrid<T>({
    apiRef: externalApiRef,
    rowsPerPageOptions,
    toolbarPagination = false,
    ...props
}: EditPanelDataGridProps<T>) {
    const apiRef = useGridApiRef(externalApiRef);
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>();
    const [msgDialogParams, setMsgDialogParams] = useState<MessageDialogState>(
        initDefaultMessageDialogState()
    );
    const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);

    const {
        editPanelContainer: containerClassName,
        pagination: paginationClass,
        root: gridRoot,
        removeEmptySpace
    } = useGridStyles();
    const { root } = useStyles();

    useEffect(() => {
        if (props.message?.open) setMsgDialogParams(props.message);
    }, [props.message]);

    const {
        components,
        columns: defaultColumns,
        headerLabel,
        autoHeight = true,
        configureColumns = (columns) => columns,
        getCurrentItemCtxMenu,
        isEnabledRowCtxClick,
        density = 'compact',
        toolbarRowCount,
        pagination: isPaginated = true,
        canEdit,
        refetch,
        gridId,
        enableContextMeny = true,
        hideFooterRowCount = true,
        disableColumnSettings = false,
        disableColumnVisibilitySaving = false,
        disableSelectionOnClick = true,
        onSelectionModelChange,
        setSelectedRows,
        componentsProps,
        contentContextMenu,
        hideToolbar = false,
        error,
        searchMode,
        hideNoRows = true,
        useRefresh = true,
        ...others
    } = props;

    const [openEditPanelContextMenu, setOpenEditPanelContextMenu] =
        useState(false);
    const [coordinatesOfMenu, setCoordinatesOfMenu] = useState<{
        X?: number;
        Y?: number;
    }>({});

    const {
        resetColumnSettings,
        updateSettingsColumn: updateSettings,
        configuredColumns,
    } = useDataGridSettings({
        gridId: props.gridId,
        columns: props.columns,
        defaultWidth: 150,
        disableColumnVisibilitySaving,
    });

    useEffect(() => {
        if (props.selectionModel && others.rows.length > 0) {
            setSelectionModel(
                (props.selectionModel ?? []).filter((id) =>
                    others.rows.some((el) => el.id === id)
                )
            );
        }
    }, [props.selectionModel, others.rows]);

    function handleContextMenu(e: any) {
        e.preventDefault();
        setOpenEditPanelContextMenu(false);

        const id = e.currentTarget.getAttribute('data-id');
        if (e?.currentTarget?.dataset?.rowindex) {
            setCoordinatesOfMenu({ X: e.clientX, Y: e.clientY });
            openCtxItemMenu(apiRef.current.getRow(id) as unknown as T);
        }

        function openCtxItemMenu(rowItem: T) {
            if (
                (!props.isEnabledRowCtxClick || props.isEnabledRowCtxClick(rowItem)) &&
                getCurrentItemCtxMenu
            ) {
                getCurrentItemCtxMenu!(rowItem);
                setOpenEditPanelContextMenu(true);
                setAnchorEl(e.target);
            }
        }
    }

    function handleResize(p: GridColumnResizeParams) {
        let newConf = configuredColumns
            .filter((i) => i.field !== 'action' && i.field !== 'selection')
            .map((i: GridColDef) => {
                if (
                    i.field === p.colDef.field ||
                    (p.colDef.field === '__tree_data_group__' &&
                        p.colDef.headerName === ((i as GridColDef).headerName ?? ''))
                )
                    return {
                        ...i,
                        width: p.width,
                        flex: undefined,
                    } as GridColDef;

                return i;
            });
        updateSettings(newConf);
    }

    function handleRefresh() {
        apiRef.current.setPage(0);
        refetch?.();
    }

    useEffect(() => {
        const handleWindowClick = (evt: MouseEvent) => {
            setOpenEditPanelContextMenu(false);
        };
        if (openEditPanelContextMenu) {
            window.addEventListener('click', handleWindowClick, {
                capture: true,
            });
            return () =>
                window.removeEventListener('click', handleWindowClick, {
                    capture: true,
                });
        }
    }, [openEditPanelContextMenu]);

    function setSelectionModelHandler(
        model: GridSelectionModel,
        details: GridCallbackDetails
    ) {
        if (others.rows?.length > 0) {
            onSelectionModelChange?.(model, details);
            setSelectionModel(model);
            if (setSelectedRows) {
                const values = props?.rows.filter(i => model.includes(i['id'])) as T[] ?? [];
                setSelectedRows(values);
            }
        }
    }

    const rowsCount =
        props.rowCount ??
        (apiRef?.current?.getRowsCount ? apiRef.current.getRowsCount() : 0);
    const defaultPageSize = density === 'compact' ? 20 : 10;
    const pagination =
        isPaginated && rowsCount > (props?.pageSize ?? defaultPageSize);

    let columns = enableContextMeny
        ? [
            ...configuredColumns.filter((i) => i.field !== 'action'),
            {
                field: 'action',
                type: 'actions',
                width: 45,
                getActions: (params: GridRowParams) => [
                    <GridActionsCellItem
                        icon={<MaterialUIIcons.MoreVertIcon test-id = {props.testId + '_moreVertIcon'}/>}
                        test-id={props.testId + '_context_menu_item'}
                        label={'actions'}
                        showInMenu={false}
                        onClick={(evt) => {
                            setOpenEditPanelContextMenu(true);
                            setAnchorEl(evt.currentTarget);
                            setCoordinatesOfMenu({ X: undefined, Y: undefined });
                            evt.stopPropagation();
                            getCurrentItemCtxMenu?.(params.row as unknown as T);
                        }}
                    />,
                ],
            },
        ]
        : [...configuredColumns];

    columns = props.checkboxSelection
        ? [
            getGridCheckboxColDef(
                selectionModel.length !== 0,
                props.disableMultipleSelection
            ),
            ...columns,
        ]
        : columns;
    const gridRef = useRefWithCallback(
        (node: any) => {
            if (props.testId) node.setAttribute('test-id', props.testId + '-tbl');
        },
        (node: any) => {
        }
    );

    const [pageSize, setPageSize] = useState(defaultPageSize);

    useEffect(() => {
        setPageSize(props.pageSize ?? defaultPageSize);
    }, [props.pageSize, defaultPageSize]);

    function pageSizeHandler(size: number) {
        if (props.setPageSize) props.setPageSize(size);
        setPageSize(size);
    }

    const selections = selectionModel?.filter((id) =>
        others.rows?.some((el) => el.id === id)
    );

    const groupingColDef = props.treeData
        ? {
            ...props.groupingColDef,
            width: columns[props.checkboxSelection ? 1 : 0].width,
        }
        : undefined;
    columns = props.treeData
        ? columns.filter(
            (i: GridColDef) => i.headerName !== groupingColDef?.headerName
        )
        : columns;
    const classesName = clsx(gridRoot, root, paginationClass, props.className, ...(others.rows.length === 0 && hideNoRows && !others.loading ? [removeEmptySpace] : []));

    const gridColumns = configureColumns(columns);
    return (
        <Box
            className={containerClassName}
            sx={{
                height: !autoHeight ? props.height ?? '40rem' : props.height,
                width: '100%',
            }}
        >
            <Controls.DataGrid
                apiRef={apiRef}
                gridRef={gridRef}
                {...others}
                groupingColDef={groupingColDef}
                autoHeight={autoHeight}
                pinnedColumns={{ right: ['action'], left: ['selection'] }}
                headerHeight={density === 'compact' ? 50 : 35}
                pageSize={pagination ? pageSize : props?.pageSize}
                columns={gridColumns}
                disableColumnFilter
                disableColumnSelector={true}
                className={classesName}
                disableSelectionOnClick={disableSelectionOnClick}
                selectionModel={selections}
                onSelectionModelChange={setSelectionModelHandler}
                rowsPerPageOptions={toolbarPagination ? rowsPerPageOptions ? rowsPerPageOptions : [10, 20, 30, 40] : [pageSize]}
                onPageSizeChange={
                    toolbarPagination
                        ? (size: number, e: any) => pageSizeHandler(size)
                        : undefined
                }
                pagination={pagination}
                density={density}
                hideFooterSelectedRowCount
                hideFooterRowCount={!(pagination || !hideFooterRowCount)}
                onColumnWidthChange={handleResize}
                hideFooterPagination={toolbarPagination || !pagination}
                hideFooter={
                    props.hideFooter ||
                    toolbarPagination ||
                    others.rows.length === 0 ||
                    (!pagination)
                }
                components={{
                    ...components,
                    Toolbar: hideToolbar ? null : EditPanelDataGridToolbar as any,
                    NoRowsOverlay: GridNoRowsCustomOverlay,
                    ErrorOverlay: CustomErrorOverlay as any,
                }}
                componentsProps={{
                    ...componentsProps,
                    toolbar: {
                        ...componentsProps?.toolbar,
                        updateColumns: updateSettings,
                        toolbarRowCount,
                        rowCount: props.rowCount,
                        toolbarPagination: toolbarPagination,
                        testId: props.testId,
                        columns: configureColumns(
                            [...configuredColumns].filter(
                                (col) => col.field !== 'action' && col.field !== 'selection'
                            )
                        ),
                        refreshTooltip: props.refreshTooltip,
                        headerName: props.headerLabel,
                        refresh: useRefresh ? componentsProps?.toolbar?.refresh ?? handleRefresh : undefined,
                        disableColumnSettings:
                            disableColumnSettings || !gridId || disableColumnVisibilitySaving,
                        resetColumnSettings,
                    },
                    errorOverlay: {
                        message: error,
                    },
                    noRowsOverlay: {
                        searchMode: props.searchMode,
                        hideNoRows: hideNoRows
                    },
                    row: {
                        onContextMenu: enableContextMeny ? handleContextMenu : undefined,
                    },
                }}
            />
            <EditPanelContextMenu
                test-id={props.testId + '_edit_panel_context_menu'}
                anchorEl={anchorEl}
                open={openEditPanelContextMenu}
                X={coordinatesOfMenu.X}
                Y={coordinatesOfMenu.Y}
                onClose={() => setOpenEditPanelContextMenu(false)}
            >
                {contentContextMenu}
            </EditPanelContextMenu>
            {msgDialogParams.open && (<MessageDialog
                open={msgDialogParams.open}
                title={msgDialogParams.title ?? ''}
                closeText='Ok'
                onClose={() => setMsgDialogParams(prev => ({ ...prev, open: false }))}
            >
                <div>
                    <p style={{ whiteSpace: 'pre-wrap', display: 'block', width: 'max-content' }}>
                        {msgDialogParams.content}
                    </p>
                </div>
            </MessageDialog>)}
        </Box>
    );
}
